import { useEffect, useRef, useState } from "react";
import { PuzzleBoard } from "../components/games/PuzzleBoard.jsx";
import { useRoom } from "../contexts/RoomContext.jsx";
import { SoundButton } from "../contexts/SoundContext.jsx";

const placementSuffix = (placement) => `${placement}º Lugar`;

export function PuzzleMultiplayerPage() {
  const {
    user,
    roomCode,
    roomData,
    isHost,
    nextRound,
    serverTimeOffset,
    awardPoints,
  } = useRoom();
  const difficulty = Number(roomData?.config?.modoQuebraCabeca) || 3;
  const roundTime =
    roomData?.config?.tempoRodada === undefined
      ? 0
      : Number(roomData.config.tempoRodada);
  const [finished, setFinished] = useState(false);
  const [solved, setSolved] = useState(false);
  const [remaining, setRemaining] = useState(roundTime);
  const finishedRef = useRef(false);
  const remainingRef = useRef(roundTime);

  useEffect(() => {
    finishedRef.current = false;
    remainingRef.current = roundTime;
    setFinished(false);
    setSolved(false);
    setRemaining(roundTime);
  }, [roomData?.rodadaAtual, roundTime]);

  const finishRound = async (didSolve) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setFinished(true);
    setSolved(didSolve);
    const placement =
      Object.values(roomData?.players || {}).filter(
        (player) => player.quebraCabecaStatus?.finalizado === true,
      ).length + 1;
    let points = 0;
    if (didSolve) {
      points = difficulty * 100;
      if (placement === 1) points += 200;
      else if (placement === 2) points += 100;
      else if (placement === 3) points += 50;
      if (roundTime > 0) points += remainingRef.current * 2;
    }
    try {
      await awardPoints(user.uid, points, {
        [`players/${user.uid}/quebraCabecaStatus`]: {
          finalizado: true,
          acertou: didSolve,
          pontosGanhos: points,
          colocacao: placement,
        },
      });
    } catch (error) {
      console.error("Erro ao salvar o resultado do quebra-cabeça:", error);
    }
  };

  useEffect(() => {
    if (roundTime === 0 || finished) return undefined;
    const updateTimer = () => {
      const serverNow = Date.now() + serverTimeOffset;
      const elapsed = Math.floor(
        (serverNow - roomData.timestampInicioRodada) / 1000,
      );
      const next = Math.max(0, roundTime - elapsed);
      remainingRef.current = next;
      setRemaining(next);
      if (next <= 0) finishRound(false);
    };
    updateTimer();
    const interval = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(interval);
  }, [roomData?.timestampInicioRodada, finished, roundTime, serverTimeOffset]);

  const players = Object.values(roomData?.players || {});
  const allFinished = players.every(
    (player) => player.quebraCabecaStatus?.finalizado === true,
  );
  const roundRanking = [...players].sort(
    (first, second) =>
      (first.quebraCabecaStatus?.colocacao || 999) -
      (second.quebraCabecaStatus?.colocacao || 999),
  );
  const persistKey =
    roomCode && roomData?.rodadaAtual && user?.uid
      ? `${roomCode}_${roomData.rodadaAtual}_${user.uid}`
      : null;

  return (
    <div className="puzzle-game-shell">
      <header className="puzzle-game-header">
        <div>
          <SoundButton variante="inline" />
          <h2>Rodada {roomData?.rodadaAtual || 1}</h2>
        </div>
        <strong className={remaining <= 15 && roundTime > 0 ? "urgent" : ""}>
          ⏱️ {roundTime > 0 ? `${remaining}s` : "∞"}
        </strong>
      </header>

      {finished ? (
        <div className="puzzle-result-scroll">
          <div
            className={`card puzzle-round-result ${solved ? "won" : "lost"}`}
          >
            <h3>
              {solved ? "🎉 Quebra-Cabeça Montado!" : "❌ O Tempo Acabou!"}
            </h3>
            <div className="puzzle-completed-image">
              <img
                src={roomData.imagemQuebraCabeca}
                alt="Quebra-Cabeça completo"
              />
            </div>
            <section className="puzzle-round-score">
              <h4>📈 Placar da Rodada:</h4>
              <ul>
                {roundRanking.map((player) => {
                  const status = player.quebraCabecaStatus;
                  const isCurrent = player.id === user.uid;
                  return (
                    <li className={isCurrent ? "current" : ""} key={player.id}>
                      <span>
                        {player.username} {isCurrent && "(Você)"}
                      </span>
                      <strong className={status?.acertou ? "success" : ""}>
                        {!status?.finalizado
                          ? "⏳ Montando..."
                          : status.acertou
                            ? `${placementSuffix(status.colocacao)} (+${status.pontosGanhos} pts)`
                            : "0 pts"}
                      </strong>
                    </li>
                  );
                })}
              </ul>
            </section>
            {isHost ? (
              <button
                className="btn btn-primary"
                onClick={nextRound}
                type="button"
              >
                {allFinished ? "Avançar Rodada ➔" : "Forçar Próxima Rodada ➔"}
              </button>
            ) : (
              <p className="info-box">Aguardando o host avançar...</p>
            )}
          </div>
        </div>
      ) : (
        <div className="puzzle-board-area">
          <PuzzleBoard
            imageUrl={roomData?.imagemQuebraCabeca}
            rows={difficulty}
            columns={difficulty}
            onComplete={() => finishRound(true)}
            persistKey={persistKey}
          />
        </div>
      )}
    </div>
  );
}
